import React, { useState, useCallback } from "react";
import { Download, FileText, Table, Image, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";

export type ExportFormat = 'csv' | 'excel' | 'pdf' | 'json';

export interface ExportColumn {
  key: string;
  title: string;
  dataIndex?: string;
  render?: (value: any, record: any) => string;
}

interface DataExportProps {
  data: any[];
  columns: ExportColumn[];
  filename?: string;
  title?: string;
  formats?: ExportFormat[];
  onExport?: (format: ExportFormat, data: any[]) => Promise<void>;
  disabled?: boolean;
  loading?: boolean;
}

export const DataExport = React.memo(({
  data,
  columns,
  filename = 'data',
  title = 'تصدير البيانات',
  formats = ['csv', 'excel', 'pdf'],
  onExport,
  disabled = false,
  loading = false
}: DataExportProps) => {
  const [exporting, setExporting] = useState<ExportFormat | null>(null);
  const { toast } = useToast();

  // تحويل البيانات إلى CSV
  const exportToCsv = useCallback(async (exportData: any[]) => {
    try {
      const headers = columns.map(col => col.title).join(',');
      const rows = exportData.map(row => {
        return columns.map(col => {
          const value = col.dataIndex ? row[col.dataIndex] : row;
          const processed = col.render ? col.render(value, row) : value;
          return `"${processed?.toString().replace(/"/g, '""') || ''}"`;
        }).join(',');
      });
      
      const csvContent = [headers, ...rows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${filename}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('خطأ في تصدير CSV:', error);
      throw error;
    }
  }, [columns, filename]);

  // تحويل البيانات إلى JSON
  const exportToJson = useCallback(async (exportData: any[]) => {
    try {
      const processedData = exportData.map(row => {
        const processed: Record<string, any> = {};
        columns.forEach(col => {
          const value = col.dataIndex ? row[col.dataIndex] : row;
          processed[col.key] = col.render ? col.render(value, row) : value;
        });
        return processed;
      });
      
      const jsonContent = JSON.stringify(processedData, null, 2);
      const blob = new Blob([jsonContent], { type: 'application/json' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${filename}.json`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('خطأ في تصدير JSON:', error);
      throw error;
    }
  }, [columns, filename]);

  // معالج التصدير
  const handleExport = useCallback(async (format: ExportFormat) => {
    if (disabled || loading || data.length === 0) return;
    
    setExporting(format);
    
    try {
      if (onExport) {
        await onExport(format, data);
      } else {
        // التصدير الافتراضي
        switch (format) {
          case 'csv':
            await exportToCsv(data);
            break;
          case 'json':
            await exportToJson(data);
            break;
          default:
            throw new Error(`تنسيق غير مدعوم: ${format}`);
        }
      }
      
      toast({
        title: "تم التصدير بنجاح",
        description: `تم تصدير ${data.length} عنصر بتنسيق ${format.toUpperCase()}`,
      });
    } catch (error) {
      toast({
        title: "خطأ في التصدير",
        description: "حدث خطأ أثناء تصدير البيانات",
        variant: "destructive",
      });
    } finally {
      setExporting(null);
    }
  }, [data, onExport, exportToCsv, exportToJson, disabled, loading, toast]);

  // أيقونات التنسيقات
  const formatIcons = {
    csv: Table,
    excel: Table,
    pdf: FileText,
    json: FileText
  };

  // أسماء التنسيقات
  const formatNames = {
    csv: 'CSV',
    excel: 'Excel',
    pdf: 'PDF',
    json: 'JSON'
  };

  if (data.length === 0) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled || loading}
          className="gap-2"
        >
          {loading || exporting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          {title}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {formats.map((format) => {
          const Icon = formatIcons[format];
          const isCurrentlyExporting = exporting === format;
          
          return (
            <DropdownMenuItem
              key={format}
              onClick={() => handleExport(format)}
              disabled={disabled || loading || isCurrentlyExporting}
              className="gap-2 cursor-pointer"
            >
              {isCurrentlyExporting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Icon className="h-4 w-4" />
              )}
              تصدير كـ {formatNames[format]}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

DataExport.displayName = "DataExport";